import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { PlatformPreviewProps } from '../types';
import { PLATFORM_CONFIG, truncateForPlatform } from '../platformConfig';
import { PreviewAvatar } from '../components/PreviewAvatar';
import { PreviewCaption } from '../components/PreviewCaption';
import { EmptyState } from '../components/EmptyState';
import { usePlaceholderImage } from '@/hooks/usePlaceholderImage';

export const TwitterPreview = ({ caption, media, channel }: PlatformPreviewProps) => {
  const { t } = useTranslation('common');
  const PLACEHOLDER_IMAGE = usePlaceholderImage();
  const config = PLATFORM_CONFIG.twitter;
  const displayCaption = truncateForPlatform(caption, 'twitter');
  const isEmpty = !caption && media.length === 0;

  const displayName = channel?.displayName ?? '';
  const handle = channel?.handle ?? '';

  if (isEmpty) return <EmptyState />;

  // Twitter shows max 4 images in grid
  const displayMedia = media.slice(0, 4);

  return (
    <div className="bg-white w-full min-w-0 max-w-[600px] mx-auto border-b border-gray-200">
      <div className="p-4 flex gap-3">
        {/* Avatar */}
        <PreviewAvatar colorClass={config.avatarColor} size="md" avatarUrl={channel?.avatarUrl} initials={channel?.initials} />

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-1 flex-wrap">
            <span className="max-w-full truncate font-bold text-gray-900 text-[15px]">{displayName}</span>
            <span className="max-w-full truncate text-gray-500 text-[15px]">@{handle}</span>
            <span className="text-gray-500 text-[15px]">-</span>
            <span className="text-gray-500 text-[15px]">{t('preview_now')}</span>
          </div>

          {/* Content */}
          {caption && (
            <PreviewCaption
              caption={displayCaption}
              hashtagColor={config.hashtagColor}
              className="text-gray-900 text-[15px] mt-1 leading-normal"
              truncated={caption.length > 150}
            />
          )}

          {/* Media Grid */}
          {displayMedia.length > 0 && (
            <div
              className={`mt-3 rounded-2xl overflow-hidden border border-gray-200 ${
                displayMedia.length === 1
                  ? 'aspect-video'
                  : displayMedia.length === 2
                    ? 'grid grid-cols-2 gap-0.5'
                    : displayMedia.length === 3
                      ? 'grid grid-cols-2 gap-0.5'
                      : 'grid grid-cols-2 gap-0.5'
              }`}
            >
              {displayMedia.length === 1 && (
                <img
                  src={displayMedia[0].url}
                  alt={t('preview_media_alt', { index: 1 })}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                  }}
                />
              )}
              {displayMedia.length === 2 &&
                displayMedia.map((item, idx) => (
                  <img
                    key={item.id || idx}
                    src={item.url}
                    alt={t('preview_media_alt', { index: idx + 1 })}
                    className="w-full aspect-square object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                    }}
                  />
                ))}
              {displayMedia.length === 3 && (
                <>
                  {/* First image on LEFT, full height */}
                  <div className="row-span-2">
                    <img
                      src={displayMedia[0].url}
                      alt={t('preview_media_alt', { index: 1 })}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                      }}
                    />
                  </div>
                  {/* Second and third images stacked on RIGHT */}
                  {displayMedia.slice(1).map((item, idx) => (
                    <img
                      key={item.id || idx}
                      src={item.url}
                      alt={t('preview_media_alt', { index: idx + 2 })}
                      className="w-full aspect-video object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                      }}
                    />
                  ))}
                </>
              )}
              {displayMedia.length === 4 &&
                displayMedia.map((item, idx) => (
                  <img
                    key={item.id || idx}
                    src={item.url}
                    alt={t('preview_media_alt', { index: idx + 1 })}
                    className="w-full aspect-video object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                    }}
                  />
                ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between mt-3 max-w-md text-gray-500">
            <button
              type="button"
              className="flex items-center gap-1.5 hover:text-blue-500 transition-colors group"
            >
              <span className="p-2 -m-2 rounded-full group-hover:bg-blue-50">
                <Icon name={ICONS.CHAT} size="sm" />
              </span>
            </button>
            <button
              type="button"
              className="flex items-center gap-1.5 hover:text-green-500 transition-colors group"
            >
              <span className="p-2 -m-2 rounded-full group-hover:bg-green-50">
                <Icon name={ICONS.REFRESH} size="sm" />
              </span>
            </button>
            <button
              type="button"
              className="flex items-center gap-1.5 hover:text-pink-500 transition-colors group"
            >
              <span className="p-2 -m-2 rounded-full group-hover:bg-pink-50">
                <Icon name={ICONS.HEART} size="sm" />
              </span>
            </button>
            <button
              type="button"
              className="flex items-center gap-1.5 hover:text-blue-500 transition-colors group"
            >
              <span className="p-2 -m-2 rounded-full group-hover:bg-blue-50">
                <Icon name={ICONS.CHART} size="sm" />
              </span>
            </button>
            <button
              type="button"
              className="flex items-center gap-1.5 hover:text-blue-500 transition-colors group"
            >
              <span className="p-2 -m-2 rounded-full group-hover:bg-blue-50">
                <Icon name={ICONS.BOOKMARK} size="sm" />
              </span>
            </button>
            <button
              type="button"
              className="flex items-center gap-1.5 hover:text-blue-500 transition-colors group"
            >
              <span className="p-2 -m-2 rounded-full group-hover:bg-blue-50">
                <Icon name={ICONS.SHARE} size="sm" />
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
